import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { LegalHeader, LegalFooter } from "./legal/Terms";
import useSEO from "../lib/useSEO";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Prefill presets — mapped from ?intent=<slug> on the URL. Keeps each buyer
// journey's context intact when Calendly is not yet wired.
const INTENT_PRESETS = {
  "walkthrough": {
    subject: "Book a Board Pack Walkthrough",
    message: "I'd like a 20-minute live walkthrough of Capittance Harmonizer using a portfolio scenario similar to my own. Best times to reach me:\n\n",
  },
  "executive-walkthrough": {
    subject: "Book an Executive Walkthrough",
    message: "I'd like a guided demonstration of the C-Suite / Executive Portal — portfolio health, board pack, NAVBOT AI. Best times to reach me:\n\n",
  },
  "transformation-assessment": {
    subject: "Request a Transformation Assessment",
    message: "I'd like a 30-minute transformation-assessment scoping call for a complex SAP / D365 / Oracle programme. Programme context:\n\n",
  },
  "team-trial": {
    subject: "Start a 14-Day Team Trial",
    message: "I'd like to start a 14-day trial for my project team. Team size / project count:\n\n",
  },
};

/**
 * Public contact form. Persists every submission on the backend and forwards
 * a copy via Resend to Requests@Capittance.com. Even if Resend delivery fails,
 * the request is safely stored in Mongo.
 */
export default function Contact() {
  useSEO({
    title: "Contact Sales & Support · 1 business-day response | Capittance",
    description: "Reach the Capittance team for pricing, subscriptions, integrations, security, refunds, partnerships and press. We reply within one business day.",
    path: "/contact",
  });
  const [params] = useSearchParams();
  const intent = params.get("intent") || "";
  const preset = INTENT_PRESETS[intent];
  const [f, setF] = useState({ name: "", email: "", subject: preset?.subject || "", message: preset?.message || "" });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  // Re-apply the preset if the ?intent= changes while the page is open.
  useEffect(() => {
    if (preset) {
      setF(prev => ({ ...prev, subject: preset.subject, message: preset.message }));
    }
  }, [intent]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.name.trim() || !/\S+@\S+\.\S+/.test(f.email) || f.message.trim().length < 5) {
      setErr("Please fill in your name, a valid email, and a message (5 chars min).");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/support/contact`, f, { timeout: 15000 });
      setOk(true);
      // Attribute the correct event to the intent that landed this visitor.
      if (intent === "transformation-assessment") trackEvent("enterprise_consultation_requested", { subject: f.subject });
      else if (intent === "executive-walkthrough" || intent === "walkthrough") trackEvent("walkthrough_booked", { intent, subject: f.subject });
      else trackEvent("walkthrough_booked", { intent: intent || "generic", subject: f.subject });
      setF({ name: "", email: "", subject: "", message: "" });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Submission failed — please email us directly.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="Contact" subtitle="We reply within 1 business day"/>
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="page-contact">
        {preset && (
          <div className="mb-6 p-4 rounded-sm border-l-4 border-[#7A1F2B] bg-[#FDF2F4] text-sm text-zinc-800" data-testid="contact-intent-banner">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7A1F2B] mb-1">
              {preset.subject}
            </div>
            <div className="text-zinc-700 leading-relaxed">
              We&rsquo;ve pre-filled the subject and a starter message for you — add your context below and we&rsquo;ll reply within one business day to schedule your call.
            </div>
          </div>
        )}
        <p className="text-sm text-zinc-500 mb-8 leading-relaxed">
          Enquiries about pricing, subscriptions, integrations, security, refunds, partnerships, and press — send them here. For the fastest response, use the form; it lands in our support queue immediately.
        </p>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Direct email</div>
            <a href="mailto:Requests@Capittance.com?subject=Enquiry" className="text-sky-700 hover:underline text-sm inline-flex items-center gap-1.5" data-testid="contact-mailto">
              <Mail size={13}/> Requests@Capittance.com
            </a>
            <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Response window</div>
            <div className="text-sm text-zinc-700">1 business day (Mon–Fri)</div>
            <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Also useful</div>
            <ul className="text-sm text-zinc-700 space-y-1">
              <li><Link to="/signup" className="hover:underline">Pricing & signup →</Link></li>
              <li><Link to="/terms" className="hover:underline">Terms of Service →</Link></li>
              <li><Link to="/privacy" className="hover:underline">Privacy Policy →</Link></li>
            </ul>
          </div>

          <form onSubmit={submit} className="md:col-span-2 space-y-3">
            {ok && (
              <div className="p-4 bg-emerald-50 text-emerald-800 text-sm rounded-sm flex items-center gap-2" data-testid="contact-success">
                <CheckCircle2 size={16}/> Thanks — we&rsquo;ve received your enquiry and will reply within one business day.
              </div>
            )}
            {err && <div className="p-3 bg-red-50 text-red-800 text-sm rounded-sm" data-testid="contact-error">{err}</div>}
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Name</span>
              <input type="text" required className="fld py-1.5 px-2 w-full" value={f.name}
                     onChange={e => setF({ ...f, name: e.target.value })} data-testid="contact-name"/>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Email</span>
              <input type="email" required className="fld py-1.5 px-2 w-full" value={f.email}
                     onChange={e => setF({ ...f, email: e.target.value })} data-testid="contact-email"/>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Subject <span className="text-zinc-400">(optional)</span></span>
              <input type="text" className="fld py-1.5 px-2 w-full" value={f.subject}
                     placeholder="Pricing enquiry / Refund request / Integration question / …"
                     onChange={e => setF({ ...f, subject: e.target.value })} data-testid="contact-subject"/>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Message</span>
              <textarea required rows={7} className="fld py-1.5 px-2 w-full resize-y" value={f.message}
                        onChange={e => setF({ ...f, message: e.target.value })} data-testid="contact-message"/>
            </label>
            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-500">We reply from Requests@Capittance.com.</div>
              <button type="submit" className="btn btn-primary" disabled={busy} data-testid="contact-submit">
                {busy ? <><Loader2 size={13} className="animate-spin"/> Sending…</> : "Send enquiry"}
              </button>
            </div>
          </form>
        </div>

        <LegalFooter />
      </main>
    </div>
  );
}
